import React, { useState, useEffect, useRef, useCallback } from 'react';
import { inventoryAPI } from '../../services/inventoryApi';

const SearchableInventorySupplierDropdown = ({
  value,
  onChange,
  displayLabel = '',
  disabled = false,
  error = false,
  placeholder = 'Select supplier',
  limit = 50,
  menuPlacement = 'up',
  // 'trade' | 'expense' | 'both' — only suppliers of exactly that type
  supplierType,
  // 'goods' (trade + both) | 'services' (expense + both) — suppliers a purchase of that kind can go to
  usableFor
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, totalCount: 0 });
  const [loading, setLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState(displayLabel);
  const [dropdownStyle, setDropdownStyle] = useState({});

  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const dropdownUidRef = useRef(null);
  const listRef = useRef(null);
  const searchTimerRef = useRef(null);
  const fetchingRef = useRef(false);
  const currentSearchRef = useRef('');

  const computeDropdownStyle = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setDropdownStyle(
      menuPlacement === 'down'
        ? { position: 'fixed', top: rect.bottom + 4, left: rect.left, width: Math.max(rect.width, 260), zIndex: 9999 }
        : { position: 'fixed', bottom: window.innerHeight - rect.top + 4, left: rect.left, width: Math.max(rect.width, 260), zIndex: 9999 }
    );
  }, [menuPlacement]);

  useEffect(() => {
    setSelectedLabel(displayLabel || '');
  }, [displayLabel]);

  // When value is set but label is empty, fetch the supplier to get its label
  useEffect(() => {
    if (value && !displayLabel) {
      inventoryAPI.getSupplierById(value)
        .then(res => {
          const s = res?.data;
          if (s?.name) setSelectedLabel(s.code ? `${s.code} - ${s.name}` : s.name);
        })
        .catch(() => {});
    }
  }, [value, displayLabel]);

  const fetchSuppliers = useCallback(async (searchTerm, page, append) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    try {
      const response = await inventoryAPI.getSuppliers({
        page,
        limit,
        search: searchTerm || undefined,
        isActive: true,
        ...(supplierType && { supplierType }),
        ...(usableFor && { usableFor }),
        sortBy: 'name',
        sortOrder: 'asc'
      });

      const nextItems = Array.isArray(response.data) ? response.data : [];
      const pag = response.pagination || {};

      setItems(prev => (append ? [...prev, ...nextItems] : nextItems));
      setPagination({
        page: pag.page || 1,
        totalPages: pag.pages || 1,
        totalCount: pag.total || 0
      });
    } catch (err) {
      console.error('SearchableInventorySupplierDropdown: fetch error', err);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [limit, supplierType, usableFor]);

  const openDropdown = () => {
    if (disabled) return;
    computeDropdownStyle();
    setIsOpen(true);
    document.dispatchEvent(new CustomEvent('slim:dropdownOpened', { detail: dropdownUidRef }));
    if (items.length === 0 && !loading) {
      currentSearchRef.current = '';
      fetchSuppliers('', 1, false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const focusInput = () => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    };
    focusInput();
    const raf = window.requestAnimationFrame(focusInput);
    const timer = setTimeout(focusInput, 30);
    return () => {
      window.cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const update = () => computeDropdownStyle();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen, computeDropdownStyle]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      currentSearchRef.current = val;
      setItems([]);
      setPagination({ page: 1, totalPages: 1, totalCount: 0 });
      fetchSuppliers(val, 1, false);
    }, 300);
  };

  const handleScroll = () => {
    const el = listRef.current;
    if (!el) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    if (nearBottom && !loading && !fetchingRef.current && pagination.page < pagination.totalPages) {
      fetchSuppliers(currentSearchRef.current, pagination.page + 1, true);
    }
  };

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    const handleOtherOpen = (e) => {
      if (e.detail !== dropdownUidRef) { setIsOpen(false); setSearch(''); }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('slim:dropdownOpened', handleOtherOpen);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('slim:dropdownOpened', handleOtherOpen);
    };
  }, []);

  const handleSelect = (supplier) => {
    const label = supplier.code ? `${supplier.code} - ${supplier.name}` : supplier.name;
    setSelectedLabel(label);
    onChange(String(supplier._id), label, supplier);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedLabel('');
    setItems([]);
    setIsOpen(false);
    setSearch('');
    onChange('', '', null);
  };

  const hasMore = pagination.page < pagination.totalPages;

  return (
    <div className="relative w-full" ref={containerRef}>
      <button
        type="button"
        onClick={openDropdown}
        disabled={disabled}
        className={`w-full px-2 py-1.5 text-xs border rounded flex items-center justify-between text-left focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${
          error ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-indigo-400 cursor-pointer'}`}
      >
        <span className={`truncate ${selectedLabel ? 'text-gray-900' : 'text-gray-400'}`}>
          {selectedLabel || placeholder}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0 ml-1">
          {selectedLabel && !disabled && (
            <span onClick={handleClear} className="text-gray-300 hover:text-gray-500 leading-none text-sm" title="Clear">×</span>
          )}
          <svg className={`w-3 h-3 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {isOpen && (
        <div style={dropdownStyle} className="bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <svg className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={searchInputRef}
                autoFocus
                type="text"
                value={search}
                onChange={handleSearchChange}
                placeholder="Search by name or code…"
                className="w-full pl-6 pr-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-gray-50"
              />
            </div>
            {pagination.totalCount > 0 && (
              <p className="text-[10px] text-gray-400 mt-1">{pagination.totalCount} supplier{pagination.totalCount !== 1 ? 's' : ''}</p>
            )}
          </div>

          <div ref={listRef} onScroll={handleScroll} className="max-h-52 overflow-y-auto">
            {!loading && items.length === 0 && (
              <p className="px-3 py-4 text-xs text-gray-500 text-center">
                {search ? 'No suppliers match your search' : 'No suppliers found'}
              </p>
            )}

            {items.map((supplier) => {
              const isSelected = String(supplier._id) === String(value);
              return (
                <button
                  key={supplier._id}
                  type="button"
                  onClick={() => handleSelect(supplier)}
                  className={`w-full flex flex-col px-3 py-1.5 text-left transition-colors ${
                    isSelected ? 'bg-indigo-50 text-indigo-700' : 'text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-xs font-medium leading-tight">{supplier.name}</span>
                  {supplier.code && <span className="text-[10px] text-gray-400">{supplier.code}</span>}
                </button>
              );
            })}

            {loading && (
              <div className="flex items-center justify-center py-3">
                <svg className="animate-spin w-3 h-3 text-indigo-600 mr-1.5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                <span className="text-xs text-gray-500">Loading…</span>
              </div>
            )}

            {!loading && hasMore && (
              <p className="text-center text-[10px] text-gray-400 py-1.5 border-t border-gray-100">Scroll for more</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableInventorySupplierDropdown;
